import { Modal, Spin, Typography } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Socket } from "socket.io-client";

import {
  useGetDetailBalanceCrypto,
  useGetDetailTransaction,
  useGetTransaction,
} from "../../../api/currency";
import { ReactComponent as CloseIcon } from "../../../common/images/svg/CloseIcon.svg";
import { ReactComponent as DepositIcon } from "../../../common/images/svg/HandDeposit.svg";
import { ReactComponent as WithDrawIcon } from "../../../common/images/svg/HandWithdraw.svg";
import { ReactComponent as NodataIcon } from "../../../common/images/svg/Nodata.svg";
import smartLogoDefault from "../../../common/images/svg/smart-logo-icon.svg";
import { CustomButton } from "../../../components/button/buttonCustom";
import { ButtonTransparent } from "../../../components/button/buttonTransparent";
import LoadingOverlay from "../../../components/loading/loadingOverlay";
import { useNotification } from "../../../components/providers/notification/useNotification";
import TextContent from "../../../pages/history/component/form/text-content";
import PaymentStatus from "../../../types/paymentStatus.type";
import { statusColors, statusNames } from "../../../utils/historyStatus.utils";
import { convertCurrency } from "../../../utils/hooks/convertRate";
import useResponsive from "../../../utils/hooks/useResponsive";
import {
  formatNumber,
  padNumber,
  shortenAddress,
} from "../../../utils/list.utils";
import { Fiat } from "../../request-money/component/form-transfer-props";
import StatusFormTransaction from "./status-form-transaction";

interface ModalDetailProps {
  selectedItem: any;
  setSelectedItem: React.Dispatch<any>;
  setDeposit: React.Dispatch<any>;
  setWithdraw: React.Dispatch<any>;
  setPrevWithdraw: React.Dispatch<any>;
  setExchange: React.Dispatch<any>;
  canAction: boolean;
  currencyOption: string;
  method: string;
  socket: Socket<any, any>;
  rates: {
    EUR: string;
    ETH: string;
    DAI: string;
    USDT: string;
    USD: string;
  };
}

const start = 0;
const pageSize = 4;

const TYPE_WITHDRAWAL = ["ONCHAIN_WITHDRAWAL", "OFFCHAIN_WITHDRAWAL"];
const TYPE_ACTION = {
  WALLET: "WALLET",
  SEND_MONEY: "SEND_MONEY",
  REQUEST_MONEY: "REQUEST_MONEY",
  DEPOSIT: "DEPOSIT",
  WITHDRAWAL: "WITHDRAWAL",
  EXCHANGE_MONEY: "EXCHANGE_MONEY",
};

const Crypto = ["USDT", "DAI", "ETH"];

const ModalDetail = ({
  selectedItem,
  setSelectedItem,
  setDeposit,
  setWithdraw,
  setPrevWithdraw,
  setExchange,
  canAction,
  currencyOption,
  method,
  socket,
  rates,
}: ModalDetailProps) => {
  const { isTablet } = useResponsive();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const transactionId = queryParams.get("transactionId");
  const isNotification = queryParams.get("isNotification");
  const typeParam = queryParams.get("type");
  const { open: openNotification } = useNotification();
  const [currentPage, setCurrentPage] = useState(1);
  const [hadData, setHadData] = useState<any>({ items: [] });
  const [params, setParams] = useState<any>({
    start,
    end: pageSize,
    tokenId: selectedItem?.id || 0,
  });
  const [detailBalance, setDetailBalance] = useState<any>();
  const [orderId, setOrderId] = useState<any>(null);
  const [prevSelectedItem, setPrevSelectedItem] = useState<any>(null);
  const [detailTransaction, setDetailTransaction] = useState<any>(null);

  const {
    data = {
      data: {
        data: [],
      },
    },
    refetch: refetchBalance,
    isFetching: loadingBalance,
  } = useGetDetailBalanceCrypto(selectedItem?.id, selectedItem?.name);

  const {
    data: dataTransactionDetail = {
      data: {
        data: {
          id: 0,
          orderId: "",
          decimals: 0,
          status: "",
          amount: "",
          chainType: "",
          symbol: "",
          beneficiary: "",
          remitter: "",
          createdAt: "",
          fees: 0,
          rejectionReason: "",
          failureReason: "",
          type: "",
        },
      },
    },
    refetch: refetchDetailTransaction,
    isFetching: fetchingDetail,
    isRefetching: refetchingDetail,
  } = useGetDetailTransaction(orderId);

  const {
    data: dataTransaction,
    refetch,
    isFetching,
  } = useGetTransaction({ ...params, id: selectedItem?.id });

  const handleDeposit = () => {
    navigate("/home");
    if (canAction) {
      setSelectedItem(null);
      setDeposit(selectedItem);
    } else {
      if (openNotification) {
        openNotification?.({
          key: "not_permission",
          message:
            "Your account is currently under review by the admin. You will be able to use this feature once the review is complete and your account is verified.",
          type: "error",
        });
      }
    }
  };

  const handleWithDraw = () => {
    navigate("/home");
    if (canAction) {
      setSelectedItem(null);
      setWithdraw(selectedItem);
      setPrevWithdraw(selectedItem);
    } else {
      if (openNotification) {
        openNotification?.({
          key: "not_permission",
          message:
            "Your account is currently under review by the admin. You will be able to use this feature once the review is complete and your account is verified.",
          type: "error",
        });
      }
    }
  };

  const handleExchange = () => {
    navigate("/home");
    if (canAction) {
      setSelectedItem(null);
      setExchange(selectedItem);
      setPrevWithdraw(selectedItem);
    } else {
      if (openNotification) {
        openNotification?.({
          key: "not_permission",
          message:
            "Your account is currently under review by the admin. You will be able to use this feature once the review is complete and your account is verified.",
          type: "error",
        });
      }
    }
  };

  const handlePageChange = () => {
    setCurrentPage((prev) => {
      const nextPage = prev + 1;
      setParams((prevParams: any) => ({
        ...prevParams,
        end: nextPage * pageSize,
      }));
      return nextPage;
    });
  };

  const handleCancel = () => {
    if (isNotification) {
      setSelectedItem(null);
      setOrderId(null);
      setPrevSelectedItem(null);
      setHadData({ items: [] });
      setDetailBalance(null);
      navigate("/home");
    } else {
      navigate("/home");
      setSelectedItem(null);
      setPrevSelectedItem(null);
      setHadData({ items: [] });
      setDetailBalance(null);
    }
  };

  const handleCancelDetail = () => {
    if (isNotification) {
      let url = `/home`;
      setSelectedItem(null);
      if (typeParam) {
        url += `?type=${typeParam}`;
      }
      navigate(url);
      setOrderId(null);
    } else {
      navigate(`/home?currency=${prevSelectedItem?.id}`);
      setOrderId(null);
      setSelectedItem(prevSelectedItem);
    }
  };

  const handleOpenDetail = (item: any) => {
    setOrderId(item?.id);
    setSelectedItem(null);
  };

  useEffect(() => {
    if (!transactionId || !selectedItem) return;
    setOrderId(Number(transactionId));
    setSelectedItem(null);
  }, [transactionId, selectedItem]);

  useEffect(() => {
    if (!selectedItem) return;
    setParams({ start, end: pageSize, tokenId: selectedItem?.id || 0 });
    setCurrentPage(1);
    setPrevSelectedItem(selectedItem);
  }, [selectedItem]);

  useEffect(() => {
    if (!dataTransaction) return;
    setHadData(dataTransaction?.data?.data);
  }, [dataTransaction]);

  useEffect(() => {
    if (!data) return;
    setDetailBalance(data?.data?.data?.[0]);
  }, [data]);

  useEffect(() => {
    if (!dataTransactionDetail) return;
    const newData = dataTransactionDetail?.data?.data;
    if (JSON.stringify(detailTransaction) !== JSON.stringify(newData)) {
      setDetailTransaction(newData);
    }
  }, [dataTransactionDetail]);

  const reasonStatusWithdraw = () => {
    switch (detailTransaction?.status) {
      case PaymentStatus.PENDING:
        return "To finalize your withdrawal, please wait while your request is being reviewed.";
      case PaymentStatus.REJECTED:
        return detailTransaction?.reason || "N/A";
      case PaymentStatus.FAILED:
        return detailTransaction?.reason || "N/A";
      case PaymentStatus.CANCELLED:
        return (
          detailTransaction?.reason ||
          "You’ve canceled the withdrawal order. Funds remain in your account."
        );
      case PaymentStatus.SUCCESS:
        return "";
      default:
        return detailTransaction?.reason || `N/A`;
    }
  };

  const reasonStatusDeposit = () => {
    switch (detailTransaction?.status) {
      case PaymentStatus.PENDING:
        return "Your deposit is pending. Please wait while we process your transaction.";
      case PaymentStatus.REJECTED:
        return detailTransaction?.reason || "N/A";
      case PaymentStatus.FAILED:
        return detailTransaction?.reason || "N/A";
      default:
        return "";
    }
  };

  useEffect(() => {
    if (!selectedItem) return;

    const handleWalletUpdated = () => {
      refetch();
      refetchBalance();
    };

    socket.on(`user.wallet.updated`, handleWalletUpdated);

    return () => {
      socket.off(`user.wallet.updated`, handleWalletUpdated);
    };
  }, [socket, selectedItem, refetch, refetchBalance]);

  useEffect(() => {
    if (!orderId) return;

    const handleDetailTransactionUpdated = () => {
      refetchDetailTransaction();
    };

    socket.on(
      `user.transactions.${orderId}.updated`,
      handleDetailTransactionUpdated
    );

    return () => {
      socket.off(
        `user.transactions.${orderId}.updated`,
        handleDetailTransactionUpdated
      );
    };
  }, [socket, orderId, refetch, refetchBalance]);

  const TitleTransaction = (type: string, outgoing: boolean) => {
    switch (type) {
      case TYPE_ACTION.WALLET:
        if (outgoing) return "Withdraw";
        else return "Deposit";
      case TYPE_ACTION.SEND_MONEY:
        if (outgoing) return "Sent via Send Money";
        else return "Received from Send Money";
      case TYPE_ACTION.REQUEST_MONEY:
        if (outgoing) return "Sent via Request Money";
        else return "Received from Request Money";
      case TYPE_ACTION.DEPOSIT:
        return "Deposit";
      case TYPE_ACTION.WITHDRAWAL:
        return "Withdraw";
      case TYPE_ACTION.EXCHANGE_MONEY:
        if (outgoing) return "Sent via Exchange Money";
        else return "Received from Exchange Money";
    }
  };

  const ChainType = (type: string, chainType: any) => {
    switch (type) {
      case TYPE_ACTION.SEND_MONEY:
        return "Send Money";
      case TYPE_ACTION.REQUEST_MONEY:
        return "Request Money";
      case TYPE_ACTION.EXCHANGE_MONEY:
        return "Exchange Money";
      default:
        return chainType;
    }
  };

  const handleSendMoney = () => {
    handleCancel();
    window.scrollTo(0, 0);
    navigate(`/send-money?currency=${selectedItem?.symbol}`);
  };

  const handleRequestMoney = () => {
    handleCancel();
    window.scrollTo(0, 0);
    navigate(`/request-money?currency=${selectedItem?.symbol}`);
  };

  return (
    <>
      {selectedItem && (
        <Modal
          open={!!selectedItem}
          onCancel={handleCancel}
          footer={null}
          destroyOnClose
          maskClosable={false}
          title={
            <>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "10px",
                  alignItems: "center",
                }}
              >
                <img
                  alt="currency"
                  src={`${process.env.REACT_APP_ROOT_URL}/${selectedItem?.iconPath}`}
                  onError={(e) => {
                    e.currentTarget.src = smartLogoDefault;
                  }}
                  style={{
                    objectFit: "cover",
                    width: isTablet ? 24 : 32,
                    height: isTablet ? 24 : 32,
                    borderRadius: "50%",
                  }}
                />
                <Typography
                  style={{
                    fontSize: isTablet ? 16 : 20,
                    fontWeight: 700,
                    color: "#C2C6CE",
                  }}
                >
                  {selectedItem?.symbol}
                </Typography>
              </div>
              <CloseIcon onClick={handleCancel} className="icon-animation" />
            </>
          }
          className="smart-modal-detail"
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: isTablet ? "16px" : "24px",
              height: "100% ",
            }}
          >
            <div className="modal-balance">
              <div className="value-balance">
                <Typography
                  style={{ fontSize: 14, fontWeight: 400, color: "white" }}
                >
                  Balance
                </Typography>
                {loadingBalance ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      minHeight: isTablet ? "66px" : "78.14px",
                    }}
                  >
                    <Spin size={"large"} className="custom-spin" />
                  </div>
                ) : (
                  <>
                    <Typography
                      style={{
                        fontSize: isTablet ? 32 : 40,
                        fontWeight: 700,
                        color: "white",
                        lineHeight: isTablet ? "36px" : "45px",
                      }}
                    >
                      {formatNumber(
                        Number(detailBalance?.totalBalance || 0),
                        detailBalance?.decimals
                      )}
                    </Typography>
                    {Crypto.includes(detailBalance?.symbol) ? (
                      <Typography.Text
                        style={{
                          fontSize: isTablet ? 14 : 16,
                          fontWeight: 500,
                          lineHeight: isTablet ? "14px" : "16px",
                          color: "white",
                        }}
                      >
                        <Typography.Text
                          style={{
                            fontSize: isTablet ? 14 : 16,
                            fontWeight: 500,
                            color: "white",
                            marginBottom: "1px",
                          }}
                        >
                          ≈
                        </Typography.Text>{" "}
                        {currencyOption === "USD" ? "$" : "€"}
                        {formatNumber(
                          convertCurrency(
                            Number(detailBalance?.totalBalance),
                            detailBalance?.symbol,
                            currencyOption,
                            rates
                          ),
                          2
                        )}
                      </Typography.Text>
                    ) : null}
                  </>
                )}
                <Typography.Text
                  style={{
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: isTablet ? "14px" : "16px",
                    color: "white",
                  }}
                >
                  Avl. Balance:{" "}
                  <Typography.Text
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: "white",
                      marginBottom: "1px",
                    }}
                  >
                    {formatNumber(
                      Number(detailBalance?.avlBalance || 0),
                      detailBalance?.decimals
                    )}
                  </Typography.Text>{" "}
                </Typography.Text>
              </div>
              {method === "CRYPTO" ? (
                <div className="button-balance" style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    text="Deposit"
                    width={"30%"}
                    onClick={handleDeposit}
                  />
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    text="Withdraw"
                    width={"30%"}
                    onClick={handleWithDraw}
                  />
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    text="Exchange"
                    width={"30%"}
                    onClick={handleExchange}
                  />
                </div>
              ) : (
                <div className="button-balance" style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    icon={<DepositIcon />}
                    text="Send Money"
                    width={"30%"}
                    onClick={handleSendMoney}
                  />
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    icon={<WithDrawIcon />}
                    text="Request Money"
                    width={"30%"}
                    onClick={handleRequestMoney}
                  />
                  <CustomButton
                    size={isTablet ? "small" : "medium"}
                    text="Exchange"
                    width={"30%"}
                    onClick={handleExchange}
                  />
                </div>
              )}
            </div>
            <div
              style={{ height: "1px", background: "rgba(255, 255, 255, 0.15)" }}
            />
            <div className={"modal-transaction"}>
              <Typography
                style={{
                  fontSize: isTablet ? 16 : 20,
                  fontWeight: 700,
                  color: "white",
                  flexShrink: 0,
                }}
              >
                Transactions
              </Typography>
              <div className={"modal-transaction-list"}>
                {!hadData?.items?.length && isFetching ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      minHeight: "488px",
                    }}
                  >
                    <Spin size="large" className="custom-spin" />
                  </div>
                ) : (
                  <>
                    {hadData?.items.length ? (
                      <>
                        {hadData?.items?.map((item: any, index: number) => (
                          <div
                            key={index}
                            className={"modal-transaction-item"}
                            onClick={() => {
                              navigate(
                                `/home?currency=${prevSelectedItem?.id}&transactionId=${item?.id}`
                              );
                              handleOpenDetail(item);
                            }}
                          >
                            <div className="modal-transaction-detail">
                              <div className="method-time">
                                <Typography className="text-method">
                                  {TitleTransaction(item.type, item?.outgoing)}
                                </Typography>
                                <div className="transaction-time">
                                  <Typography className="text-transaction-id">
                                    {item?.orderId
                                      ? item.orderIdTxT ?? `#${String(item.orderId).padStart(
                                        4,
                                        "0"
                                      )}`
                                      : ""}
                                  </Typography>
                                  <div className="dot-transaction"></div>
                                  <Typography className="text-transaction-id">
                                    {item?.createdAt
                                      ? moment(item?.createdAt).format(
                                        "HH:mm:ss DD/MM/YYYY"
                                      )
                                      : ""}
                                  </Typography>
                                </div>
                              </div>
                              <div className="value-status">
                                <Typography
                                  className="text-value"
                                  style={{
                                    color:
                                      item?.status === PaymentStatus.REJECTED ||
                                        item?.status === PaymentStatus.CANCELLED ||
                                        item?.status === PaymentStatus.FAILED
                                        ? "white"
                                        : !item?.outgoing
                                          ? "#18B853"
                                          : "#EC3333",
                                  }}
                                >
                                  {item?.status === PaymentStatus.REJECTED ||
                                    item?.status === PaymentStatus.CANCELLED ||
                                    item?.status === PaymentStatus.FAILED
                                    ? ""
                                    : !item?.outgoing
                                      ? "+"
                                      : "-"}
                                  {formatNumber(
                                    Number(item?.amount || 0),
                                    detailBalance?.decimals
                                  )}
                                </Typography>
                                <Typography
                                  style={{
                                    color:
                                      item?.status === PaymentStatus.REJECTED
                                        ? statusColors[item?.status]
                                        : "rgba(255, 255, 255, 0.6)",
                                  }}
                                  className="text-status"
                                >
                                  {statusNames[item?.status] || ""}
                                </Typography>
                              </div>
                            </div>
                            {item?.reason ? (
                              <div className="modal-transaction-reason">
                                <Typography
                                  style={{
                                    fontSize: isTablet ? 12 : 14,
                                    fontWeight: 400,
                                    color: "rgba(255, 255, 255, 0.6)",
                                    lineHeight: "20px",
                                  }}
                                  className="text-status"
                                >
                                  Reason: {item?.reason || ""}
                                </Typography>
                              </div>
                            ) : null}
                          </div>
                        ))}
                        {hadData?.page < hadData?.totalPages ? (
                          <>
                            {isFetching ? (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  minHeight: isTablet ? "32px" : "40px",
                                }}
                              >
                                <Spin
                                  size={isTablet ? "default" : "large"}
                                  className="custom-spin"
                                />
                              </div>
                            ) : (
                              <ButtonTransparent
                                size={isTablet ? "small" : "medium"}
                                text={"Load More"}
                                width={"100%"}
                                textClassName="text-button-back"
                                onClick={handlePageChange}
                              />
                            )}
                          </>
                        ) : null}
                      </>
                    ) : (
                      <div className="empty-transaction">
                        <div className="ant-empty-image">
                          <NodataIcon />
                        </div>
                        <div className="ant-empty-description">No data</div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}
      <Modal
        open={refetchingDetail ? !!orderId : !!orderId && !fetchingDetail}
        onCancel={handleCancelDetail}
        footer={null}
        destroyOnClose
        maskClosable={false}
        title={
          <>
            <Typography
              style={{
                fontSize: isTablet ? 16 : 20,
                fontWeight: 700,
                color: "#C2C6CE",
              }}
            >
              {TitleTransaction(
                detailTransaction?.type,
                detailTransaction?.outgoing
              )}
            </Typography>
            <CloseIcon
              onClick={handleCancelDetail}
              className="icon-animation"
            />
          </>
        }
        className="send-money-modal"
      >
        <div>
          <div className="form-history fix-bottom">
            <div className="modal-balance">
              <div className="value-balance">
                <Typography style={{ fontSize: 16, color: "white" }}>
                  Quantity
                </Typography>
                <Typography.Text className={"text-gradient"}>
                  {detailTransaction?.type === TYPE_ACTION.WALLET ? (
                    <>
                      {TYPE_WITHDRAWAL.includes(detailTransaction?.orderType)
                        ? `${detailTransaction?.status ===
                          PaymentStatus.REJECTED ||
                          detailTransaction?.status ===
                          PaymentStatus.CANCELLED
                          ? ""
                          : "-"
                        }${formatNumber(
                          Number(detailTransaction?.amount),
                          detailTransaction?.decimals
                        )}`
                        : `${detailTransaction?.status === PaymentStatus.SUCCESS
                          ? "+"
                          : ""
                        }${formatNumber(
                          Number(detailTransaction?.amount),
                          detailTransaction?.decimals
                        )}`}{" "}
                    </>
                  ) : (
                    <>
                      {detailTransaction?.outgoing
                        ? `-${formatNumber(
                          Number(detailTransaction?.amount),
                          detailTransaction?.decimals
                        )}`
                        : `+${formatNumber(
                          Number(detailTransaction?.amount),
                          detailTransaction?.decimals
                        )}`}
                    </>
                  )}
                  <Typography.Text
                    className="text-gradient"
                    style={{
                      fontSize: isTablet ? "20px" : "24px",
                      lineHeight: isTablet ? "20px" : "24px",
                    }}
                  >
                    {" "}
                    {detailTransaction?.symbol}
                  </Typography.Text>
                </Typography.Text>
                <StatusFormTransaction
                  status={detailTransaction?.status}
                  content={
                    TYPE_WITHDRAWAL.includes(detailTransaction?.orderType)
                      ? reasonStatusWithdraw()
                      : reasonStatusDeposit()
                  }
                />
              </div>
            </div>
            <div className="form-history-content">
              <TextContent
                label="Chain Type"
                value={ChainType(
                  detailTransaction?.type,
                  detailTransaction?.chainType
                )}
              />
              {detailTransaction?.type === TYPE_ACTION.EXCHANGE_MONEY ? (
                <></>
              ) : detailTransaction?.type === TYPE_ACTION.WALLET ? (
                <>
                  {TYPE_WITHDRAWAL.includes(detailTransaction?.orderType) ? (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? "Beneficiary"
                          : "Address"
                      }
                      value={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : shortenAddress(detailTransaction?.address)
                      }
                      realValue={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : detailTransaction?.address
                      }
                      canCopy
                    />
                  ) : (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? "Address"
                          : "Remitter"
                      }
                      value={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? shortenAddress(detailTransaction?.address)
                          : detailTransaction?.remitterFullName
                      }
                      realValue={detailTransaction?.address}
                      canCopy={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                      }
                    />
                  )}
                  <TextContent
                    label="Order"
                    value={detailTransaction?.orderId}
                    color="rgba(242, 138, 40, 1)"
                  />
                </>
              ) : detailTransaction?.type === TYPE_ACTION.SEND_MONEY ? (
                <>
                  <TextContent
                    label={
                      detailTransaction?.outgoing ? "Beneficiary" : "Remitter"
                    }
                    value={
                      detailTransaction?.outgoing
                        ? detailTransaction?.beneficiaryEmail
                        : detailTransaction?.remitterEmail
                    }
                  />
                  <TextContent
                    label="Order"
                    value={
                      detailTransaction?.orderId ||
                      padNumber(detailTransaction?.id)
                    }
                    color="rgba(242, 138, 40, 1)"
                  />
                </>
              ) : detailTransaction?.type === TYPE_ACTION.WITHDRAWAL ? (
                <>
                  {TYPE_WITHDRAWAL.includes(detailTransaction?.orderType) ? (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? "Beneficiary"
                          : "Address"
                      }
                      value={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : shortenAddress(detailTransaction?.address)
                      }
                      realValue={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : detailTransaction?.address
                      }
                      canCopy
                    />
                  ) : (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? "Address"
                          : "Remitter"
                      }
                      value={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? shortenAddress(detailTransaction?.address)
                          : detailTransaction?.remitterFullName
                      }
                      realValue={detailTransaction?.address}
                      canCopy={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                      }
                    />
                  )}
                  <TextContent
                    label="Order"
                    value={detailTransaction?.orderId}
                    color="rgba(242, 138, 40, 1)"
                  />
                </>
              ) : detailTransaction?.type === TYPE_ACTION.DEPOSIT ? (
                <>
                  {TYPE_WITHDRAWAL.includes(detailTransaction?.orderType) ? (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? "Beneficiary"
                          : "Address"
                      }
                      value={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : shortenAddress(detailTransaction?.address)
                      }
                      realValue={
                        detailTransaction?.orderType === "OFFCHAIN_WITHDRAWAL"
                          ? detailTransaction?.beneficiaryFullName
                          : detailTransaction?.address
                      }
                      canCopy
                    />
                  ) : (
                    <TextContent
                      label={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? "Address"
                          : "Remitter"
                      }
                      value={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                          ? shortenAddress(detailTransaction?.address)
                          : detailTransaction?.remitterFullName
                      }
                      realValue={detailTransaction?.address}
                      canCopy={
                        detailTransaction?.orderType === "ONCHAIN_DEPOSIT"
                      }
                    />
                  )}
                  <TextContent
                    label="Order"
                    value={detailTransaction?.orderId}
                    color="rgba(242, 138, 40, 1)"
                  />
                </>
              ) : (
                <>
                  <TextContent
                    label={"Remitter"}
                    value={detailTransaction?.remitterEmail}
                  />
                  <TextContent
                    label="Order"
                    value={
                      detailTransaction?.orderId ||
                      padNumber(detailTransaction?.id)
                    }
                    color="rgba(242, 138, 40, 1)"
                  />
                </>
              )}
            </div>
            <div className="form-history-content border-top">
              <TextContent
                label={detailTransaction?.type === TYPE_ACTION.EXCHANGE_MONEY ? "Order Time" : "Time"}
                value={moment(detailTransaction?.createdAt).format(
                  "HH:mm:ss DD/MM/YYYY"
                )}
              />
            </div>
          </div>
          {detailTransaction?.type === TYPE_ACTION.WALLET || detailTransaction?.type === TYPE_ACTION.DEPOSIT || detailTransaction?.type === TYPE_ACTION.WITHDRAWAL ? (
            <>
              {TYPE_WITHDRAWAL.includes(detailTransaction?.orderType) ? (
                <div className="form-withdraw-button">
                  <CustomButton
                    size={isTablet ? "medium" : "large"}
                    text={"View Withdraw Details"}
                    width={"100%"}
                    onClick={() => {
                      const isFiat = Fiat.includes(detailTransaction?.currency);
                      navigate(
                        `/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${detailTransaction?.walletOrderId}&type=crypto-withdrawals`
                      )
                    }
                    }
                  />
                </div>
              ) : null}
            </>
          ) : detailTransaction?.type === TYPE_ACTION.SEND_MONEY ? (
            <>
              {detailTransaction?.outgoing ? (
                <div className="form-withdraw-button">
                  <CustomButton
                    size={isTablet ? "medium" : "large"}
                    text={"View Send Money Details"}
                    width={"100%"}
                    onClick={() => {
                      const isFiat = Fiat.includes(detailTransaction?.currency);
                      navigate(
                        `/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${detailTransaction?.paymentRequestId}&type=send-money`
                      )
                    }}
                  />
                </div>
              ) : null}
            </>
          ) : detailTransaction?.type === TYPE_ACTION.EXCHANGE_MONEY ? (
            <div className="form-withdraw-button">
              <CustomButton
                size={isTablet ? "medium" : "large"}
                text={"View Exchange Money Details"}
                width={"100%"}
                onClick={() => {
                  const isFiat = Fiat.includes(detailTransaction?.currency);
                  navigate(
                    `/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${detailTransaction?.walletOrderId}&type=exchange`
                  )
                }}
              />
            </div>
          ) : (
            <div className="form-withdraw-button">
              <CustomButton
                size={isTablet ? "medium" : "large"}
                text={"View Request Money Details"}
                width={"100%"}
                onClick={() => {
                  const isFiat = Fiat.includes(detailTransaction?.currency);
                  navigate(
                    `/history?tab=${isFiat ? 'fiat' : 'crypto'}&orderId=${detailTransaction?.paymentOrderId}&type=request-money`
                  )
                }
                }
              />
            </div>
          )}
        </div>
      </Modal>
      <LoadingOverlay loading={fetchingDetail && !refetchingDetail} time={0} />
    </>
  );
};

export default ModalDetail;
